import { Route, Routes } from 'react-router-dom';
import { UserRole } from '@teentack/shared';
import { HomePage } from '@/pages/HomePage';
import { JobsPage } from '@/pages/JobsPage';
import { LoginPage } from '@/pages/LoginPage';
import { RegisterPage } from '@/pages/RegisterPage';
import { ProfilePage } from '@/pages/ProfilePage';
import { NotificationsPage } from '@/pages/NotificationsPage';
import { MessagesPage } from '@/pages/MessagesPage';
import { TrackingPage } from '@/pages/TrackingPage';
import { MoneyPage } from '@/pages/MoneyPage';
import { TeenShortlistPage } from '@/pages/TeenShortlistPage';
import { SafetyPage } from '@/pages/SafetyPage';
import { SupportPage } from '@/pages/SupportPage';
import { GoalsPage } from '@/pages/GoalsPage';
import { SettingsPage } from '@/pages/SettingsPage';
import { InsightsPage } from '@/pages/InsightsPage';
import { GuardianRequestsPage } from '@/pages/GuardianRequestsPage';
import { ConsentRequestsPage } from '@/pages/ConsentRequestsPage';
import { ParentDashboardPage } from '@/pages/ParentDashboardPage';
import { ParentNetworkPage } from '@/pages/ParentNetworkPage';
import { ApplicantsPage } from '@/pages/ApplicantsPage';
import { MyWorkPage } from '@/pages/MyWorkPage';
import { TeenDashboardPage } from '@/pages/TeenDashboardPage';
import { SavedJobsPage } from '@/pages/SavedJobsPage';
import { MyJobsPage } from '@/pages/MyJobsPage';
import { PosterDashboardPage } from '@/pages/PosterDashboardPage';
import { PostJobPage } from '@/pages/PostJobPage';
import { JobDetailPage } from '@/pages/JobDetailPage';
import { VerifyOtpPage } from '@/pages/VerifyOtpPage';
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage';
import { ResetPasswordPage } from '@/pages/ResetPasswordPage';
import { PendingApprovalPage } from '@/pages/PendingApprovalPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { ProtectedRoute } from '@/auth/ProtectedRoute';
import { SiteHeader } from '@/components/SiteHeader';
import { SiteFooter } from '@/components/SiteFooter';
import { useScrollToHash } from '@/hooks/use-scroll-to-hash';

export function App() {
  useScrollToHash();

  return (
    <div className="flex min-h-full flex-col bg-canvas">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-50 focus:rounded-xl focus:bg-brand-600 focus:px-4 focus:py-2 focus:font-semibold focus:text-white"
      >
        Skip to content
      </a>

      <SiteHeader />

      <main id="main" className="flex-1">
        <Routes>
          <Route path="/" element={<HomePage />} />

          {/* Public auth surface [FR-M01.1, FR-M01.7, FR-M01.8] */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/verify" element={<VerifyOtpPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* Signed in but not necessarily active [FR-M01.2] */}
          {/* A pending teen still needs to finish their profile, so this route
              does not require an ACTIVE account. Admins hold no profile row and
              are managed from the admin app. [FR-M01.5] */}
          <Route
            path="/profile"
            element={
              <ProtectedRoute
                roles={[UserRole.TEEN, UserRole.PARENT, UserRole.JOB_POSTER]}
                requireActive={false}
              >
                <ProfilePage />
              </ProtectedRoute>
            }
          />

          {/* The help centre reads signed out — a question about how guardian
              approval works should not need an account to answer. */}
          <Route path="/support" element={<SupportPage />} />

          {/* Getting help. Deliberately does NOT require an active account:
              a teen waiting on approval, or one who has been suspended, is
              exactly who most needs to be able to reach someone. */}
          <Route
            path="/safety"
            element={
              <ProtectedRoute
                roles={[UserRole.TEEN, UserRole.PARENT, UserRole.JOB_POSTER]}
                requireActive={false}
              >
                <SafetyPage />
              </ProtectedRoute>
            }
          />

          {/* How it is going, per teen. [Parent step 23] */}
          <Route
            path="/insights"
            element={
              <ProtectedRoute roles={[UserRole.PARENT]}>
                <InsightsPage />
              </ProtectedRoute>
            }
          />

          {/* Goals a guardian sets with their teen. [Parent step 24] */}
          <Route
            path="/goals"
            element={
              <ProtectedRoute roles={[UserRole.PARENT]}>
                <GoalsPage />
              </ProtectedRoute>
            }
          />

          {/* Privacy, and the terms in force. [Parent steps 3, 11, 27] */}
          <Route
            path="/settings"
            element={
              <ProtectedRoute roles={[UserRole.PARENT]}>
                <SettingsPage />
              </ProtectedRoute>
            }
          />

          {/* A guardian reading their teen's shortlist. [Parent step 7] */}
          <Route
            path="/teen-shortlist"
            element={
              <ProtectedRoute roles={[UserRole.PARENT]}>
                <TeenShortlistPage />
              </ProtectedRoute>
            }
          />

          {/* Money. Every role has a ledger: teens are paid, employers pay,
              and guardians read their teens' alongside their own. */}
          <Route
            path="/money"
            element={
              <ProtectedRoute roles={[UserRole.TEEN, UserRole.PARENT, UserRole.JOB_POSTER]}>
                <MoneyPage />
              </ProtectedRoute>
            }
          />

          {/* Work under way — the read-only side of progress and location. */}
          <Route
            path="/tracking"
            element={
              <ProtectedRoute roles={[UserRole.PARENT, UserRole.JOB_POSTER]}>
                <TrackingPage />
              </ProtectedRoute>
            }
          />

          {/* Messages. The optional :conversationId keeps the list and the open
              thread on one route, so a link from a notification lands directly
              in the right conversation. */}
          <Route
            path="/messages"
            element={
              <ProtectedRoute roles={[UserRole.TEEN, UserRole.PARENT, UserRole.JOB_POSTER]}>
                <MessagesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/messages/:conversationId"
            element={
              <ProtectedRoute roles={[UserRole.TEEN, UserRole.PARENT, UserRole.JOB_POSTER]}>
                <MessagesPage />
              </ProtectedRoute>
            }
          />

          {/* Every signed-in role has an inbox. A pending teen gets one too —
              the guardian-approval result is the first thing to land in it. */}
          <Route
            path="/notifications"
            element={
              <ProtectedRoute
                roles={[UserRole.TEEN, UserRole.PARENT, UserRole.JOB_POSTER]}
                requireActive={false}
              >
                <NotificationsPage />
              </ProtectedRoute>
            }
          />

          {/* Parents only — where a minor's account is unblocked. [FR-M01.2] */}
          <Route
            path="/guardian"
            element={
              <ProtectedRoute roles={[UserRole.PARENT]} requireActive={false}>
                <GuardianRequestsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/pending"
            element={
              <ProtectedRoute requireActive={false}>
                <PendingApprovalPage />
              </ProtectedRoute>
            }
          />

          {/* Browsing is open to every active account — teens look for work,
              parents look on their behalf, posters see the market. [Teen 7] */}
          <Route
            path="/jobs"
            element={
              <ProtectedRoute roles={[UserRole.TEEN, UserRole.PARENT, UserRole.JOB_POSTER]}>
                <JobsPage />
              </ProtectedRoute>
            }
          />

          {/* Job poster: dashboard and job creation. [Poster steps 4-10]
              `/jobs/new` is declared before `/jobs/:jobId` so "new" is never
              read as a job id. */}
          <Route
            path="/poster"
            element={
              <ProtectedRoute roles={[UserRole.JOB_POSTER]} requireActive={false}>
                <PosterDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-jobs"
            element={
              <ProtectedRoute roles={[UserRole.JOB_POSTER]} requireActive={false}>
                <MyJobsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/jobs/new"
            element={
              <ProtectedRoute roles={[UserRole.JOB_POSTER]}>
                <PostJobPage />
              </ProtectedRoute>
            }
          />

          {/* One job in full — teens apply from here, owners publish. */}
          <Route
            path="/jobs/:jobId"
            element={
              <ProtectedRoute roles={[UserRole.TEEN, UserRole.PARENT, UserRole.JOB_POSTER]}>
                <JobDetailPage />
              </ProtectedRoute>
            }
          />

          {/* The teen console. `requireActive` is off: a teen waiting on their
              guardian must still be able to see what is blocking them and
              finish their profile. [FR-M01.2] */}
          <Route
            path="/teen"
            element={
              <ProtectedRoute roles={[UserRole.TEEN]} requireActive={false}>
                <TeenDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-work"
            element={
              <ProtectedRoute roles={[UserRole.TEEN]} requireActive={false}>
                <MyWorkPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/saved"
            element={
              <ProtectedRoute roles={[UserRole.TEEN]} requireActive={false}>
                <SavedJobsPage />
              </ProtectedRoute>
            }
          />

          {/* The parent console. `requireActive` is off throughout: a guardian
              must be able to answer approvals even while their own account is
              still settling. */}
          <Route
            path="/parent"
            element={
              <ProtectedRoute roles={[UserRole.PARENT]} requireActive={false}>
                <ParentDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/network"
            element={
              <ProtectedRoute roles={[UserRole.PARENT]} requireActive={false}>
                <ParentNetworkPage />
              </ProtectedRoute>
            }
          />

          {/* Parent: per-job consent, distinct from account approval. [Parent 9-10] */}
          <Route
            path="/approvals"
            element={
              <ProtectedRoute roles={[UserRole.PARENT]} requireActive={false}>
                <ConsentRequestsPage />
              </ProtectedRoute>
            }
          />

          {/* Job poster: review applicants and assign. [Poster 11-18] */}
          <Route
            path="/applicants"
            element={
              <ProtectedRoute roles={[UserRole.JOB_POSTER]}>
                <ApplicantsPage />
              </ProtectedRoute>
            }
          />

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>

      <SiteFooter />
    </div>
  );
}
